import React, { useEffect, useRef, useState } from "react";

/**
 * FloatingYouTubePlayer
 *
 * A single, persistent YouTube iframe that lives inside its docked slot when
 * the slot is meaningfully visible in the viewport, and slides into a
 * compact floating position when the user has scrolled away AND the video
 * is actively playing.
 *
 * Design principles:
 *   – The iframe DOM node is NEVER unmounted/remounted between docked and
 *     floating states. Only the wrapping div's classes change. React
 *     reconciles the class attribute in place, so the same YouTube player
 *     instance is preserved and playback continues seamlessly with no
 *     restart, no duplicate audio and no duplicate iframe.
 *   – Playback state is detected via the YouTube IFrame Player API
 *     (enablejsapi=1). Floating only activates after a real PLAYING event.
 *   – Visibility is driven by IntersectionObserver on the slot placeholder
 *     with ~100px hysteresis + a 200ms debounce to absorb scroll jitter.
 *   – Close button dismisses the floating experience for the current
 *     playback session. A fresh PLAYING event resets the dismissal.
 */

let ytApiPromise = null;

const loadYouTubeIframeAPI = () => {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (ytApiPromise) return ytApiPromise;

  ytApiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) {
      resolve(window.YT);
      return;
    }
    const prevCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prevCallback === "function") {
        try {
          prevCallback();
        } catch (_) {
          /* noop */
        }
      }
      resolve(window.YT);
    };
    if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      tag.async = true;
      document.head.appendChild(tag);
    }
  });
  return ytApiPromise;
};

const extractVideoId = (embedUrl) => {
  if (!embedUrl) return null;
  const match = embedUrl.match(/\/embed\/([^?&/]+)/);
  return match ? match[1] : null;
};

const FloatingYouTubePlayer = ({ embedUrl, title }) => {
  const videoId = extractVideoId(embedUrl);

  const slotRef = useRef(null);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const debounceRef = useRef(null);

  const [slotVisible, setSlotVisible] = useState(true);
  const [hasEverPlayed, setHasEverPlayed] = useState(false);
  const [userClosed, setUserClosed] = useState(false);
  const [isFloating, setIsFloating] = useState(false);

  // ─── Slot visibility — float when less than 50% remains visible ──────────
  useEffect(() => {
    const el = slotRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setSlotVisible(entry.intersectionRatio >= 0.7);
      },
      { rootMargin: "-96px 0px 0px 0px", threshold: [0, 0.7, 1], },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ─── Debounced docked / floating transition (~200ms) ─────────────────────
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const shouldFloat = hasEverPlayed && !slotVisible && !userClosed;
    if (shouldFloat === isFloating) return;
    debounceRef.current = setTimeout(() => {
      setIsFloating(shouldFloat);
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [slotVisible, hasEverPlayed, userClosed, isFloating]);

  // ─── Bind the YouTube IFrame API to the (already-rendered) iframe ────────
  useEffect(() => {
    if (!videoId) return undefined;
    let cancelled = false;

    loadYouTubeIframeAPI().then((YT) => {
      if (cancelled || !YT || !iframeRef.current) return;
      try {
        playerRef.current = new YT.Player(iframeRef.current, {
          events: {
            onStateChange: (event) => {
              if (event.data === YT.PlayerState.PLAYING) {
                setHasEverPlayed(true);
                // A fresh playback resets any earlier dismissal.
                setUserClosed(false);
              }
            },
          },
        });
      } catch (_) {
        // If binding fails, floating logic simply never activates —
        // the iframe still plays normally in its docked position.
      }
    });

    return () => {
      cancelled = true;
      if (
        playerRef.current &&
        typeof playerRef.current.destroy === "function"
      ) {
        try {
          playerRef.current.destroy();
        } catch (_) {
          /* noop */
        }
        playerRef.current = null;
      }
    };
  }, [videoId]);

  if (!videoId) return null;

  const origin =
    typeof window !== "undefined" ? window.location.origin : "";
  const iframeSrc = `https://www.youtube.com/embed/${videoId}?enablejsapi=1&origin=${encodeURIComponent(
    origin,
  )}`;

  const dockedClass = "absolute inset-0 transition-opacity duration-300";
  const floatingClass =
    // Mobile: near-full-width, sitting just below the site header
    "fixed left-3 right-3 top-24 z-40 aspect-video " +
    // Desktop: compact card at bottom-right
    "md:left-auto md:right-6 md:bottom-6 md:top-auto md:w-[420px] " +
    "overflow-hidden bg-charcoal border border-charcoal/25 shadow-md " +
    "transition-opacity duration-300";

  return (
    <div
      ref={slotRef}
      data-testid="floating-youtube-slot"
      className="mt-12 mx-auto aspect-video w-full max-w-2xl overflow-hidden border border-charcoal/15 bg-charcoal relative"
    >
      {isFloating && (
        <div
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center text-white/55 text-[11px] tracking-[0.28em] uppercase pointer-events-none"
        >
          Playing in floating view
        </div>
      )}

      <div
        data-testid={isFloating ? "yt-player-floating" : "yt-player-docked"}
        className={isFloating ? floatingClass : dockedClass}
      >
        <iframe
          ref={iframeRef}
          src={iframeSrc}
          title={title || "YouTube video player"}
          className="w-full h-full block"
          allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
        {isFloating && (
          <button
            type="button"
            onClick={() => setUserClosed(true)}
            aria-label="Close floating player"
            data-testid="floating-player-close"
            className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-charcoal/85 text-white/90 hover:text-white hover:bg-charcoal border border-white/20 text-base leading-none"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

export default FloatingYouTubePlayer;
